import * as React from 'react';

/**
 * A small CommonMark subset rendered straight to React elements. It never
 * emits raw HTML, so untrusted skill text can only ever become text nodes and
 * the handful of elements built here. Links keep http(s) and mailto only.
 */

type Inline =
  | { kind: 'text'; text: string }
  | { kind: 'code'; text: string }
  | { kind: 'strong'; children: Inline[] }
  | { kind: 'em'; children: Inline[] }
  | { kind: 'link'; href: string; children: Inline[] };

type Block =
  | { kind: 'heading'; level: number; children: Inline[] }
  | { kind: 'paragraph'; children: Inline[] }
  | { kind: 'code'; text: string; lang: string | null }
  | { kind: 'quote'; children: Block[] }
  | { kind: 'list'; ordered: boolean; items: Block[][] }
  | { kind: 'rule' }
  | { kind: 'table'; header: Inline[][]; rows: Inline[][][] };

const SAFE_PROTOCOLS = ['http:', 'https:', 'mailto:'];

/** Absolute http(s) or mailto only; anything else renders as plain text. */
export function safeHref(raw: string): string | null {
  const trimmed = raw.trim();
  if (trimmed.length === 0 || /[\s<>"']/.test(trimmed)) return null;
  try {
    const url = new URL(trimmed);
    return SAFE_PROTOCOLS.includes(url.protocol) ? url.href : null;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Inline parsing
// ---------------------------------------------------------------------------

function parseInline(text: string): Inline[] {
  const out: Inline[] = [];
  let buffer = '';
  let i = 0;
  const flush = () => {
    if (buffer) out.push({ kind: 'text', text: buffer });
    buffer = '';
  };

  while (i < text.length) {
    const ch = text[i] as string;

    if (ch === '\\' && i + 1 < text.length) {
      buffer += text[i + 1];
      i += 2;
      continue;
    }

    if (ch === '`') {
      const run = text.slice(i).match(/^`+/)?.[0] ?? '`';
      const close = text.indexOf(run, i + run.length);
      if (close !== -1) {
        flush();
        out.push({ kind: 'code', text: text.slice(i + run.length, close).trim() });
        i = close + run.length;
        continue;
      }
    }

    if (ch === '[') {
      const link = matchLink(text, i);
      if (link) {
        flush();
        const href = safeHref(link.href);
        const children = parseInline(link.label);
        out.push(href ? { kind: 'link', href, children } : { kind: 'text', text: link.label });
        i = link.end;
        continue;
      }
    }

    if (ch === '*' || ch === '_') {
      const strong = matchDelimited(text, i, `${ch}${ch}`);
      if (strong) {
        flush();
        out.push({ kind: 'strong', children: parseInline(strong.inner) });
        i = strong.end;
        continue;
      }
      const em = matchDelimited(text, i, ch);
      if (em) {
        flush();
        out.push({ kind: 'em', children: parseInline(em.inner) });
        i = em.end;
        continue;
      }
    }

    buffer += ch;
    i += 1;
  }
  flush();
  return out;
}

function matchDelimited(
  text: string,
  start: number,
  delimiter: string,
): { inner: string; end: number } | null {
  if (!text.startsWith(delimiter, start)) return null;
  const from = start + delimiter.length;
  if (from >= text.length || /\s/.test(text[from] as string)) return null;
  let close = text.indexOf(delimiter, from);
  while (close !== -1) {
    if (!/\s/.test(text[close - 1] as string)) {
      return { inner: text.slice(from, close), end: close + delimiter.length };
    }
    close = text.indexOf(delimiter, close + 1);
  }
  return null;
}

function matchLink(
  text: string,
  start: number,
): { label: string; href: string; end: number } | null {
  let depth = 0;
  let i = start;
  for (; i < text.length; i += 1) {
    if (text[i] === '[') depth += 1;
    else if (text[i] === ']') {
      depth -= 1;
      if (depth === 0) break;
    }
  }
  if (depth !== 0 || text[i + 1] !== '(') return null;
  // Balanced parentheses inside the target, so `(a(b))` reads as one href.
  let parens = 0;
  let close = -1;
  for (let j = i + 2; j < text.length; j += 1) {
    if (text[j] === '(') parens += 1;
    else if (text[j] === ')') {
      if (parens === 0) {
        close = j;
        break;
      }
      parens -= 1;
    }
  }
  if (close === -1) return null;
  const label = text.slice(start + 1, i);
  const target = text.slice(i + 2, close).trim();
  const href = target.split(/\s+/)[0] ?? '';
  return { label, href, end: close + 1 };
}

// ---------------------------------------------------------------------------
// Block parsing
// ---------------------------------------------------------------------------

const FENCE = /^(\s{0,3})(`{3,}|~{3,})\s*([\w+-]*)\s*$/;
const HEADING = /^(#{1,6})\s+(.*?)\s*#*\s*$/;
const RULE = /^\s{0,3}([-*_])(\s*\1){2,}\s*$/;
const BULLET = /^(\s*)[-*+]\s+(.*)$/;
const NUMBERED = /^(\s*)\d{1,9}[.)]\s+(.*)$/;
const QUOTE = /^\s{0,3}>\s?(.*)$/;
const TABLE_DIVIDER = /^\s*\|?\s*:?-{1,}:?\s*(\|\s*:?-{1,}:?\s*)*\|?\s*$/;

function isBlank(line: string): boolean {
  return line.trim().length === 0;
}

function splitCells(line: string): string[] {
  const trimmed = line.trim().replace(/^\|/, '').replace(/\|$/, '');
  return trimmed.split(/(?<!\\)\|/).map((cell) => cell.trim().replace(/\\\|/g, '|'));
}

export function parseMarkdown(source: string): Block[] {
  return parseBlocks(source.replace(/\r\n?/g, '\n').split('\n'));
}

function parseBlocks(lines: string[]): Block[] {
  const blocks: Block[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i] as string;

    if (isBlank(line)) {
      i += 1;
      continue;
    }

    const fence = line.match(FENCE);
    if (fence) {
      const marker = fence[2] as string;
      const body: string[] = [];
      i += 1;
      while (i < lines.length && !(lines[i] as string).trim().startsWith(marker)) {
        body.push(lines[i] as string);
        i += 1;
      }
      i += 1;
      blocks.push({ kind: 'code', text: body.join('\n'), lang: fence[3] || null });
      continue;
    }

    const heading = line.match(HEADING);
    if (heading) {
      blocks.push({
        kind: 'heading',
        level: (heading[1] as string).length,
        children: parseInline(heading[2] as string),
      });
      i += 1;
      continue;
    }

    if (RULE.test(line)) {
      blocks.push({ kind: 'rule' });
      i += 1;
      continue;
    }

    if (QUOTE.test(line)) {
      const inner: string[] = [];
      while (i < lines.length && QUOTE.test(lines[i] as string)) {
        inner.push((lines[i] as string).match(QUOTE)?.[1] ?? '');
        i += 1;
      }
      blocks.push({ kind: 'quote', children: parseBlocks(inner) });
      continue;
    }

    if (BULLET.test(line) || NUMBERED.test(line)) {
      const ordered = NUMBERED.test(line) && !BULLET.test(line);
      const pattern = ordered ? NUMBERED : BULLET;
      const indent = (line.match(pattern)?.[1] ?? '').length;
      const items: Block[][] = [];
      while (i < lines.length) {
        const current = lines[i] as string;
        const match = current.match(pattern);
        if (!match || (match[1] as string).length !== indent) break;
        const item: string[] = [match[2] as string];
        i += 1;
        // Continuation lines are anything indented deeper than the marker.
        while (i < lines.length) {
          const next = lines[i] as string;
          if (isBlank(next)) {
            const after = lines[i + 1];
            if (after !== undefined && leadingSpaces(after) > indent && !isBlank(after)) {
              item.push('');
              i += 1;
              continue;
            }
            break;
          }
          if (leadingSpaces(next) <= indent) break;
          item.push(next.slice(Math.min(leadingSpaces(next), indent + 2)));
          i += 1;
        }
        items.push(parseBlocks(item));
        if (i < lines.length && isBlank(lines[i] as string)) {
          const after = lines[i + 1];
          if (after !== undefined && after.match(pattern)?.[1]?.length === indent) i += 1;
        }
      }
      blocks.push({ kind: 'list', ordered, items });
      continue;
    }

    if (
      line.includes('|') &&
      lines[i + 1] !== undefined &&
      TABLE_DIVIDER.test(lines[i + 1] as string)
    ) {
      const header = splitCells(line).map(parseInline);
      const rows: Inline[][][] = [];
      i += 2;
      while (
        i < lines.length &&
        !isBlank(lines[i] as string) &&
        (lines[i] as string).includes('|')
      ) {
        rows.push(splitCells(lines[i] as string).map(parseInline));
        i += 1;
      }
      blocks.push({ kind: 'table', header, rows });
      continue;
    }

    const paragraph: string[] = [line.trim()];
    i += 1;
    while (i < lines.length) {
      const next = lines[i] as string;
      if (
        isBlank(next) ||
        FENCE.test(next) ||
        HEADING.test(next) ||
        RULE.test(next) ||
        QUOTE.test(next) ||
        BULLET.test(next) ||
        NUMBERED.test(next)
      ) {
        break;
      }
      paragraph.push(next.trim());
      i += 1;
    }
    blocks.push({ kind: 'paragraph', children: parseInline(paragraph.join('\n')) });
  }

  return blocks;
}

function leadingSpaces(line: string): number {
  return line.match(/^\s*/)?.[0].length ?? 0;
}

// ---------------------------------------------------------------------------
// Rendering
// ---------------------------------------------------------------------------

function renderInline(nodes: Inline[]): React.ReactNode[] {
  return nodes.map((node, index) => {
    const key = `${node.kind}-${index}`;
    switch (node.kind) {
      case 'text':
        return renderText(node.text, key);
      case 'code':
        return (
          <code key={key} className="rounded bg-muted px-1 py-0.5 font-mono text-[0.85em]">
            {node.text}
          </code>
        );
      case 'strong':
        return (
          <strong key={key} className="font-semibold">
            {renderInline(node.children)}
          </strong>
        );
      case 'em':
        return <em key={key}>{renderInline(node.children)}</em>;
      case 'link':
        return (
          <a
            key={key}
            href={node.href}
            target="_blank"
            rel="noreferrer noopener"
            className="underline underline-offset-2 hover:text-foreground"
          >
            {renderInline(node.children)}
          </a>
        );
    }
  });
}

/** Soft line breaks inside a paragraph become <br>, everything else stays text. */
function renderText(text: string, key: string): React.ReactNode {
  const parts = text.split('\n');
  if (parts.length === 1) return <React.Fragment key={key}>{text}</React.Fragment>;
  return (
    <React.Fragment key={key}>
      {parts.map((part, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: parsed nodes have no identity; the tree is rebuilt per source
        <React.Fragment key={`${key}-${index}`}>
          {index > 0 ? <br /> : null}
          {part}
        </React.Fragment>
      ))}
    </React.Fragment>
  );
}

const TH_CLASS = 'border-b px-2 py-1 text-left font-semibold';

const HEADING_CLASS: Readonly<Record<number, string>> = {
  1: 'text-xl font-semibold mt-6 first:mt-0',
  2: 'text-lg font-semibold mt-6 first:mt-0',
  3: 'text-base font-semibold mt-5 first:mt-0',
  4: 'text-sm font-semibold mt-4 first:mt-0',
  5: 'text-sm font-medium mt-4 first:mt-0',
  6: 'text-sm font-medium text-muted-foreground mt-4 first:mt-0',
};

function renderBlocks(blocks: Block[]): React.ReactNode[] {
  return blocks.map((block, index) => {
    const key = `${block.kind}-${index}`;
    switch (block.kind) {
      case 'heading':
        return React.createElement(
          `h${block.level}`,
          { key, className: HEADING_CLASS[block.level] },
          renderInline(block.children),
        );
      case 'paragraph':
        return (
          <p key={key} className="leading-relaxed">
            {renderInline(block.children)}
          </p>
        );
      case 'code':
        return (
          <pre
            key={key}
            className="overflow-x-auto rounded-md border bg-muted/50 p-3 font-mono text-xs leading-relaxed"
            data-lang={block.lang ?? undefined}
          >
            <code>{block.text}</code>
          </pre>
        );
      case 'quote':
        return (
          <blockquote
            key={key}
            className="border-l-2 border-border pl-4 text-muted-foreground [&>*+*]:mt-2"
          >
            {renderBlocks(block.children)}
          </blockquote>
        );
      case 'list':
        return React.createElement(
          block.ordered ? 'ol' : 'ul',
          {
            key,
            className: `${block.ordered ? 'list-decimal' : 'list-disc'} space-y-1 pl-6`,
          },
          block.items.map((item, itemIndex) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: parsed nodes have no identity; the tree is rebuilt per source
            <li key={`${key}-${itemIndex}`} className="[&>*+*]:mt-1">
              {renderBlocks(item)}
            </li>
          )),
        );
      case 'rule':
        return <hr key={key} className="my-4 border-border" />;
      case 'table':
        return (
          <div key={key} className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr>
                  {block.header.map((cell, cellIndex) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: parsed nodes have no identity; the tree is rebuilt per source
                    <th key={`${key}-h-${cellIndex}`} className={TH_CLASS}>
                      {renderInline(cell)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {block.rows.map((row, rowIndex) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: parsed nodes have no identity; the tree is rebuilt per source
                  <tr key={`${key}-r-${rowIndex}`}>
                    {row.map((cell, cellIndex) => (
                      // biome-ignore lint/suspicious/noArrayIndexKey: parsed nodes have no identity; the tree is rebuilt per source
                      <td key={`${key}-r-${rowIndex}-${cellIndex}`} className="border-b px-2 py-1">
                        {renderInline(cell)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
    }
  });
}

export interface MarkdownProps {
  source: string;
  className?: string;
}

/** Renders `source` as a styled document. Safe by construction: no HTML passthrough. */
export function Markdown({ source, className }: MarkdownProps) {
  const blocks = React.useMemo(() => parseMarkdown(source), [source]);
  return (
    <div className={className ? `markdown ${className}` : 'markdown'} data-testid="markdown">
      <div className="space-y-3 text-sm text-foreground">{renderBlocks(blocks)}</div>
    </div>
  );
}
